import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { titleOf } from "@/config";
import { Label11, StatusBadge, Bar, usd, pct, shortDate } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Info } from "lucide-react";

// Hiring terms (backend/assignment_routes.py, TEAM_ROLES_PLAN.md section 4, design_handoff_team_roles/): what a company
// needs someone for (an objective), the fixed fee in US dollars for an independent professional, and the task's AI
// budget (the objective's budget). An independent professional agrees before a task is theirs; a team member has no fee.
// Crewbench records the fee and never pays it.

export const BLANK_TASK = { project_id: "", objective_id: "", title: "", description: "", fee: "", budget: "", responsibilities: "" };
const amount = (v) => { const n = Number(String(v).trim()); return String(v).trim() !== "" && Number.isFinite(n) && n > 0 ? n : null; };
const join = (xs) => (xs.length < 2 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs.at(-1)}`);

// what's still missing, in words: ["what for", "the hiring cost", "the task budget"]
export function taskMissing(v, kind) {
  const out = [];
  if (!v.objective_id || (v.objective_id === "new" && !v.title.trim())) out.push("what for");
  if (kind === "independent" && amount(v.fee) == null) out.push("the hiring cost");
  if (amount(v.budget) == null) out.push("the task budget");
  return out;
}
export const missingLine = (missing, verb = "send it") => (missing.length ? `Add ${join(missing)} to ${verb}.` : "");

// the API's TaskTerms (assignment_routes.TaskTerms)
export function taskPayload(v, kind) {
  const created = v.objective_id === "new";
  return {
    project_id: v.project_id || null, objective_id: created ? null : v.objective_id,
    new_objective: created ? { title: v.title.trim(), description: v.description.trim() } : null,
    fee_usd: kind === "independent" ? amount(v.fee) : null, budget_usd: amount(v.budget),
  };
}

function MoneyField({ id, label, help, value, onChange, testid }) {
  return (
    <div className="flex flex-col gap-1.5 min-w-0">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <span className="absolute inset-y-0 start-2.5 flex items-center text-muted-foreground pointer-events-none" aria-hidden="true">$</span>
        <Input id={id} inputMode="decimal" className="ps-6 tabular-nums" value={value} placeholder="0" data-testid={testid}
          onChange={(e) => onChange(e.target.value)} />
      </div>
      <span className="text-sm text-muted-foreground">{help}</span>
    </div>
  );
}

// What for, the hiring cost (independent professionals) and the task budget; Responsibilities stays optional
export function TaskFields({ value: v, onChange, kind, who = "They", responsibilities = true }) {
  const { data: projects } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });
  const { data: objs } = useQuery({ queryKey: ["objectives"], queryFn: async () => (await api.get("/objectives")).data });
  const set = (patch) => onChange({ ...v, ...patch });
  const list = (projects?.projects || []);
  const choices = (objs?.objectives || []).filter((o) => o.status !== "done" && (!v.project_id || o.project_id === v.project_id));
  const pick = (oid) => {
    const o = choices.find((x) => x.id === oid);
    // an objective's budget is the task budget: start from it, the founder can change it here
    set({ objective_id: oid, ...(o && !v.budget && o.budget_usd ? { budget: String(o.budget_usd) } : {}) });
  };
  return (
    <div className="flex flex-col gap-3.5 pt-4 border-t">
      <div className="flex flex-col gap-0.5">
        <span className="font-display text-[15px] leading-[1.45] tracking-[0.02em] font-semibold">What for</span>
        <span className="text-sm text-muted-foreground">A project and objective. {who === "They" ? "They're assigned to it when they join." : `${who} is assigned once they agree.`}</span>
      </div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3">
        <div className="flex flex-col gap-1.5 min-w-0">
          <Label id="task-project">Project</Label>
          <Select value={v.project_id || undefined} onValueChange={(p) => set({ project_id: p, objective_id: "" })}>
            <SelectTrigger aria-labelledby="task-project" data-testid="task-project"><SelectValue placeholder="Choose a project" /></SelectTrigger>
            <SelectContent>{list.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5 min-w-0">
          <Label id="task-objective">Objective</Label>
          <Select value={v.objective_id || undefined} onValueChange={pick} disabled={!v.project_id && list.length > 0}>
            <SelectTrigger aria-labelledby="task-objective" data-testid="task-objective"><SelectValue placeholder="Choose an objective" /></SelectTrigger>
            <SelectContent>
              {choices.map((o) => <SelectItem key={o.id} value={o.id}>{o.title}</SelectItem>)}
              <SelectItem value="new" data-testid="task-objective-new">New objective…</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {v.objective_id === "new" && (
        <div className="flex flex-col gap-3 p-3.5 rounded-lg border">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="task-title">New objective title</Label>
            <Input id="task-title" value={v.title} placeholder="e.g. Cookie banner text" data-testid="task-title" onChange={(e) => set({ title: e.target.value })} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="task-description">Short description</Label>
            <Textarea id="task-description" rows={2} className="min-h-0" value={v.description} placeholder="What done looks like, in a sentence or two."
              onChange={(e) => set({ description: e.target.value })} />
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        {kind === "independent" && <MoneyField id="task-fee" label="Hiring cost" help="A fixed price for this task, in US dollars." value={v.fee}
          onChange={(fee) => set({ fee })} testid="task-fee" />}
        <MoneyField id="task-budget" label="Task budget" help="The objective's AI budget. Sessions stop when it's used up." value={v.budget}
          onChange={(budget) => set({ budget })} testid="task-budget" />
      </div>
      {responsibilities && (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="task-resp">Responsibilities <span className="font-normal text-muted-foreground">(optional)</span></Label>
          <Input id="task-resp" value={v.responsibilities} placeholder="e.g. Terms, privacy and refund policy" onChange={(e) => set({ responsibilities: e.target.value })} />
        </div>
      )}
      {kind === "independent" && (
        <p className="m-0 flex items-start gap-2 text-sm text-muted-foreground"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          Crewbench doesn't pay the professional; this records what you agreed. They see these terms and tick "I agree" before the task is theirs.</p>
      )}
    </div>
  );
}

// Hire for another task (a Team card): the same three things as an invite (POST /team/{id}/tasks). `initial`: fields
// filled in already (the assistant's Fill in the hiring form: the objective it created and its budget)
export function HireDialog({ m, initial, onOpenChange }) {
  const qc = useQueryClient();
  const [v, setV] = useState(BLANK_TASK);
  useEffect(() => { if (m) setV({ ...BLANK_TASK, ...initial }); }, [m, initial]);
  const kind = m?.engagement === "employee" ? "employee" : "independent";
  const first = (m?.name || m?.email || "").split(" ")[0];
  const missing = taskMissing(v, kind);
  const hire = useMutation({
    mutationFn: async () => (await api.post(`/team/${m.id}/tasks`, { ...taskPayload(v, kind), responsibilities: v.responsibilities.trim() })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["team"] }); qc.invalidateQueries({ queryKey: ["objectives"] });
      toast.success(kind === "independent" ? `Sent to ${first}. It shows "Waiting for their agreement" until they agree.` : `${first} has the task now. We told them.`);
      onOpenChange(false);
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  return (
    <Dialog open={!!m} onOpenChange={onOpenChange}>
      <DialogContent className="gap-[18px] max-h-[calc(100vh-48px)] overflow-y-auto">
        <div className="flex flex-col gap-1">
          <DialogTitle>{kind === "independent" ? `Hire ${m?.name || m?.email} for another task` : `Add a task for ${m?.name || m?.email}`}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {kind === "independent" ? `The same terms as an invite. ${first} agrees in the app before they're assigned.` : `${first} sees the new task on their home.`}
          </DialogDescription>
        </div>
        <form className="flex flex-col gap-[18px]" onSubmit={(e) => { e.preventDefault(); if (!missing.length && !hire.isPending) hire.mutate(); }}>
          <TaskFields value={v} onChange={setV} kind={kind} who={first} />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-sm text-muted-foreground">{missingLine(missing)}</span>
            <div className="flex gap-2 ms-auto">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" aria-disabled={missing.length > 0} disabled={hire.isPending} data-testid="hire-send">Send to {first}</Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// A new fee for an agreed task, with a short reason: the professional agrees in the app (POST /assignments/{id}/fee)
// Hire someone for this (an objective's page): the objective is set; pick who, then the fee (an independent
// professional) and the task budget. Same request as Hire for another task (POST /team/{id}/tasks).
export function HireForObjective({ objective: o, taken, open, onOpenChange }) {
  const qc = useQueryClient();
  const { data: team } = useQuery({ queryKey: ["team"], queryFn: async () => (await api.get("/team")).data, enabled: open });
  const [who, setWho] = useState("");
  const [fee, setFee] = useState("");
  const [budget, setBudget] = useState("");
  const [resp, setResp] = useState("");
  useEffect(() => { if (open) { setWho(""); setFee(""); setBudget(o?.budget_usd ? String(o.budget_usd) : ""); setResp(""); } }, [open, o]);
  const people = (team?.members || []).filter((m) => m.role === "coder" && !taken.includes(m.id));
  const m = people.find((x) => x.id === who);
  const kind = m?.engagement === "employee" ? "employee" : "independent";
  const first = (m?.name || m?.email || "").split(" ")[0];
  const missing = [...(kind === "independent" && amount(fee) == null ? ["the hiring cost"] : []), ...(amount(budget) == null ? ["the task budget"] : [])];
  const ready = !!m && !missing.length;
  const hire = useMutation({
    mutationFn: async () => (await api.post(`/team/${m.id}/tasks`, {
      objective_id: o.id, fee_usd: kind === "independent" ? amount(fee) : null, budget_usd: amount(budget), responsibilities: resp.trim(),
    })).data,
    onSuccess: () => {
      ["team", "objective", "objectives"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      toast.success(kind === "independent" ? `Sent to ${first}. It shows "Waiting for their agreement" until they agree.` : `${first} has the task now. We told them.`);
      onOpenChange(false);
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-[18px] max-h-[calc(100vh-48px)] overflow-y-auto" data-testid="hire-objective-dialog">
        <div className="flex flex-col gap-1">
          <DialogTitle>Hire someone for {o?.title}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            An independent professional agrees in the app before they're assigned. A team member has the task at once. Someone new? <Link to="/founder/team" className="underline underline-offset-2 hover:text-foreground">Invite them from Team</Link>.
          </DialogDescription>
        </div>
        <form className="flex flex-col gap-[18px]" onSubmit={(e) => { e.preventDefault(); if (ready && !hire.isPending) hire.mutate(); }}>
          <div className="flex flex-col gap-1.5">
            <Label id="hire-who">Who</Label>
            {people.length ? (
              <Select value={who || undefined} onValueChange={setWho}>
                <SelectTrigger aria-labelledby="hire-who" data-testid="hire-who"><SelectValue placeholder="Choose someone on your team" /></SelectTrigger>
                <SelectContent>{people.map((x) => <SelectItem key={x.id} value={x.id}>{x.name || x.email} · {titleOf(x)}</SelectItem>)}</SelectContent>
              </Select>
            ) : <span className="text-sm text-muted-foreground">Everyone on your team is on it already.</span>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {kind === "independent" && <MoneyField id="hire-fee" label="Hiring cost" help="A fixed price for this task, in US dollars." value={fee} onChange={setFee} testid="hire-fee" />}
            <MoneyField id="hire-budget" label="Task budget" help="The objective's AI budget. Sessions stop when it's used up." value={budget} onChange={setBudget} testid="hire-budget" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="hire-resp">Responsibilities <span className="font-normal text-muted-foreground">(optional)</span></Label>
            <Input id="hire-resp" value={resp} placeholder="e.g. Terms, privacy and refund policy" onChange={(e) => setResp(e.target.value)} />
          </div>
          {kind === "independent" && (
            <p className="m-0 flex items-start gap-2 text-sm text-muted-foreground"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              Crewbench doesn't pay the professional; this records what you agreed. They see these terms and tick "I agree" before the task is theirs.</p>
          )}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-sm text-muted-foreground">{!m ? "Choose who to hire." : missingLine(missing)}</span>
            <div className="flex gap-2 ms-auto">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" aria-disabled={!ready} disabled={hire.isPending} data-testid="hire-objective-send">{m ? `Send to ${first}` : "Send"}</Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function FeeDialog({ task, person, onOpenChange }) {
  const qc = useQueryClient();
  const [fee, setFee] = useState("");
  const [reason, setReason] = useState("");
  useEffect(() => { setFee(""); setReason(""); }, [task]);
  const first = (person?.name || person?.email || "").split(" ")[0];
  const ok = amount(fee) != null && amount(fee) !== task?.fee_usd && reason.trim().length >= 3;
  const change = useMutation({
    mutationFn: async () => (await api.post(`/assignments/${task.id}/fee`, { fee_usd: amount(fee), reason: reason.trim() })).data,
    onSuccess: () => { ["team", "objective", "objectives"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); toast.success(`New fee sent to ${first}. The agreed ${usd(task.fee_usd, 0)} stands until they agree.`); onOpenChange(false); },
    onError: (e) => toast.error(apiErr(e)),
  });
  return (
    <Dialog open={!!task} onOpenChange={onOpenChange}>
      <DialogContent className="gap-[18px]">
        <div className="flex flex-col gap-1">
          <DialogTitle>Change the fee for {task?.objective}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {first} agreed to {usd(task?.fee_usd, 0)}. They agree to the new fee in the app; until then the agreed fee stands. Agreed fees are never deleted.
          </DialogDescription>
        </div>
        <form className="flex flex-col gap-[18px]" onSubmit={(e) => { e.preventDefault(); if (ok && !change.isPending) change.mutate(); }}>
          <MoneyField id="fee-new" label="New hiring cost" help="A fixed price for the task, in US dollars." value={fee} onChange={setFee} testid="fee-new" />
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="fee-reason">Why it changes</Label>
            <Input id="fee-reason" value={reason} maxLength={500} placeholder="e.g. The cookie policy was added to the task." data-testid="fee-reason"
              onChange={(e) => setReason(e.target.value)} />
            <span className="text-sm text-muted-foreground">{first} sees this with the new fee.</span>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" aria-disabled={!ok} disabled={change.isPending} data-testid="fee-send">Send the new fee</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const day = (iso) => shortDate(iso).toUpperCase();

// A task's state as a status word: AGREED 3 OCT, WAITING FOR THEIR AGREEMENT (founder) / WAITING FOR YOU, ASSIGNED 1 OCT
export function TaskState({ t, mine }) {
  if (t.status === "offered") return <StatusBadge tone="warning" label={mine ? "Waiting for you" : "Waiting for their agreement"} />;
  if (t.status === "agreed") return <StatusBadge tone="info" label={`Agreed ${day(t.agreed_at)}`} />;
  return <StatusBadge tone="muted" label={t.offered_at ? `Assigned ${day(t.offered_at)}` : "Assigned"} />;
}

// One task: the objective and project, the state, then fee, task budget and AI spent with a bar. `mine`: the
// professional's own view ("Your fee"); a team member's task has no fee.
export function TaskFigures({ t, mine }) {
  const fee = t.engagement === "independent" ? (t.fee_usd != null ? `${usd(t.fee_usd, 0)}, fixed` : "No fee agreed") : "No fee";
  const full = t.budget_usd > 0 && t.spent_usd >= t.budget_usd;
  return (
    <>
      <div className="grid grid-cols-3 gap-2 text-sm tabular-nums">
        {(!mine || t.engagement === "independent") && <span className="flex flex-col"><span className="text-muted-foreground">{mine ? "Your fee" : "Fee"}</span><span className="text-base">{fee}</span></span>}
        <span className="flex flex-col"><span className="text-muted-foreground">Task budget</span><span className="text-base">{usd(t.budget_usd, t.budget_usd % 1 ? 2 : 0)}</span></span>
        <span className="flex flex-col"><span className="text-muted-foreground">AI spent</span><span className="text-base">{usd(t.spent_usd)}</span></span>
      </div>
      {t.status !== "offered" && <Bar pct={Math.min(100, pct(t.spent_usd, t.budget_usd))} full={full} />}
    </>
  );
}

// The professional's agreement to a task offered in the app, or to a fee change (POST /me/tasks/{id}/agree)
export function AgreePanel({ t }) {
  const qc = useQueryClient();
  const [ticked, setTicked] = useState(false);
  const fee = !!t.pending;
  const agree = useMutation({
    mutationFn: async () => (await api.post(`/me/tasks/${t.id}/agree`, { agree: true })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["my-tasks"] }); qc.invalidateQueries({ queryKey: ["objectives"] }); toast.success(fee ? "You agreed to the new fee." : "You agreed. The task is yours."); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const id = `agree-${t.id}`;
  return (
    <div className="flex flex-col gap-2.5 p-3.5 rounded-lg border border-gr-warning" data-testid={`agree-panel-${t.id}`}>
      <span className="text-sm text-gr-ink-secondary [text-wrap:pretty]">
        {fee ? <>{t.offered_by || "Your founder"} offers {usd(t.pending.fee_usd, 0)} instead of {usd(t.fee_usd, 0)}: “{t.pending.reason}”. The agreed fee stands until you agree.</>
          : <>{t.offered_by || "Your founder"} offers this task for a fixed fee of {usd(t.fee_usd, 0)}, with an AI budget of {usd(t.budget_usd, 0)}. You're assigned once you agree.</>}
      </span>
      <div className="flex items-start gap-2.5">
        <Checkbox id={id} checked={ticked} onCheckedChange={(x) => setTicked(x === true)} className="mt-0.5" data-testid={`agree-tick-${t.id}`} />
        <Label htmlFor={id} className="font-semibold">I agree to these hiring terms</Label>
      </div>
      <span className="text-sm text-muted-foreground">Crewbench isn't a party to this agreement and doesn't pay. The time you agree is kept.</span>
      <div><Button size="sm" aria-disabled={!ticked} disabled={agree.isPending} data-testid={`agree-btn-${t.id}`}
        onClick={() => { if (ticked && !agree.isPending) agree.mutate(); }}>Agree</Button></div>
    </div>
  );
}

// "Your tasks" on the professional's home (GET /me/tasks): only their own fee
export function YourTasks({ engagement }) {
  const { data } = useQuery({ queryKey: ["my-tasks"], queryFn: async () => (await api.get("/me/tasks")).data, refetchInterval: 15000 });
  const tasks = data?.tasks || [];
  return (
    <section className="flex flex-col gap-3" data-testid="your-tasks">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h2 className="font-display text-[15px] leading-[1.45] tracking-[0.02em] font-semibold">Your tasks</h2>
        <span className="text-sm text-muted-foreground">{engagement === "employee" ? "Assigned by your founders" : "Only you and your founders see your fee"}</span>
      </div>
      {tasks.length ? (
        <div className="flex flex-col gap-px bg-gr-hairline rounded-[10px] overflow-hidden">
          {tasks.map((t) => (
            <div key={t.id} className="bg-card px-[18px] py-3.5 flex flex-col gap-2.5" data-testid={`my-task-${t.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <span className="flex flex-col min-w-0 max-w-[640px]">
                  <span className="font-semibold">{t.objective}</span>
                  {t.project && <span className="text-sm text-muted-foreground">{t.project}</span>}
                  {t.description && <span className="text-sm text-gr-ink-secondary mt-1">{t.description}</span>}
                </span>
                <TaskState t={t} mine />
              </div>
              <TaskFigures t={t} mine />
              {(t.status === "offered" || t.pending) && <AgreePanel t={t} />}
            </div>
          ))}
        </div>
      ) : <div className="bg-card rounded-[10px] px-5 py-4 text-muted-foreground">No tasks yet. Your founders give you tasks from Team.</div>}
    </section>
  );
}

// The terms on the invite page (POST /auth/invite `task`)
export function InviteTerms({ task }) {
  return (
    <div className="flex flex-col rounded-lg border overflow-hidden tabular-nums" data-testid="invite-task">
      <div className="flex flex-col gap-0.5 px-3.5 py-3">
        <Label11>The task</Label11>
        <span className="font-semibold">{task.objective}</span>
        {task.project && <span className="text-sm text-muted-foreground">{task.project}</span>}
        {task.description && <span className="text-sm text-gr-ink-secondary mt-1">{task.description}</span>}
      </div>
      <div className="flex flex-wrap gap-px bg-gr-hairline border-t">
        {task.engagement === "independent" && (
          <div className="flex-[1_1_140px] bg-card px-3.5 py-2.5 flex flex-col"><Label11>Agreed fee</Label11>
            <span className="text-[15px] font-semibold">{usd(task.fee_usd, task.fee_usd % 1 ? 2 : 0)}</span>
            <span className="text-sm text-muted-foreground">Fixed price, US dollars</span></div>
        )}
        <div className="flex-[1_1_140px] bg-card px-3.5 py-2.5 flex flex-col"><Label11>Task budget</Label11>
          <span className="text-[15px] font-semibold">{usd(task.budget_usd, task.budget_usd % 1 ? 2 : 0)}</span>
          <span className="text-sm text-muted-foreground">For AI. Sessions stop when it's used up.</span></div>
      </div>
    </div>
  );
}

export function AgreeBox({ task, checked, onChange }) {
  return (
    <div className="flex flex-col gap-1.5 p-3.5 rounded-lg border">
      <div className="flex items-start gap-2.5">
        <Checkbox id="invite-agree" checked={checked} onCheckedChange={(x) => onChange(x === true)} className="mt-0.5" data-testid="invite-agree" />
        <Label htmlFor="invite-agree" className="font-semibold">I agree to these hiring terms</Label>
      </div>
      <span className="text-sm text-muted-foreground ps-[26px]">
        The task, the {usd(task.fee_usd, task.fee_usd % 1 ? 2 : 0)} fixed fee and the {usd(task.budget_usd, task.budget_usd % 1 ? 2 : 0)} task budget above.
        Crewbench isn't a party to this agreement and doesn't pay. The time you agree is kept.
      </span>
    </div>
  );
}

